import type { ChipTone } from '@patchbay/ui'
import type { BrandFeature, LaneCount } from '../../lib/api/generated'

export type ApiLane = LaneCount['lane']

export type QueueLaneSort = 'who_owes' | 'oldest_waiting' | 'newest_activity' | 'due_sla' | 'customer'

export const QUEUE_SORT_OPTIONS: readonly { value: QueueLaneSort; label: string }[] = [
  { value: 'who_owes', label: 'who owes the move' },
  { value: 'oldest_waiting', label: 'oldest waiting' },
  { value: 'newest_activity', label: 'newest activity' },
  { value: 'due_sla', label: 'due / SLA soonest' },
  { value: 'customer', label: 'customer A-Z' },
] as const

export function queueSortLabel(sort: QueueLaneSort): string {
  return QUEUE_SORT_OPTIONS.find((option) => option.value === sort)?.label ?? 'who owes the move'
}

export interface QueueLaneDefinition {
  api: ApiLane
  route: string
  label: string
  key?: `${number}`
  /** UI-only union of reply_owed and action_owed; it is not a new server lane. */
  composite?: boolean
  /** Brand feature required to offer this lane in navigation. Deep links are still enforced by the API. */
  feature?: BrandFeature
}

export interface QueueLanePresentation {
  id: ApiLane
  label: string
  tone: ChipTone
}

export type NeedsUsFilter = 'all' | 'replies' | 'actions'

export const NEEDS_US_NOW: QueueLaneDefinition = {
  // Kept only so the common screen type remains a lane definition. Composite reads never send this value.
  api: 'reply_owed',
  route: 'needs-us-now',
  label: 'Needs us now',
  composite: true,
}

/**
 * Route slugs stay readable while the API uses enum-safe underscores. The first nine entries are
 * deliberately ordered to match the queue's 1-9 shortcuts (scope §5.1).
 */
export const QUEUE_LANES: readonly QueueLaneDefinition[] = [
  { api: 'reply_owed', route: 'reply-owed', label: 'Reply owed', key: '1' },
  { api: 'action_owed', route: 'action-owed', label: 'Action owed by us', key: '2' },
  { api: 'waiting_customer', route: 'waiting-customer', label: 'Waiting on customer', key: '3' },
  { api: 'waiting_contractor', route: 'waiting-contractor', label: 'Waiting on contractor', key: '4', feature: 'contractors' },
  { api: 'wrapup', route: 'wrap-up', label: 'Wrap-up' },
  // #763: an external wait (the assigned tech owes the visit report). Not part of Needs us until it escalates.
  { api: 'tech_report', route: 'tech-report', label: 'Waiting on tech report', feature: 'contractors' },
  { api: 'scheduled', route: 'scheduled', label: 'Scheduled', key: '5', feature: 'schedule' },
  // #549: not-yet-due work we own. No number key, so the 1-9 shortcuts stay on the lanes above and below.
  { api: 'later', route: 'later', label: 'Later' },
  { api: 'no_time_logged', route: 'no-time', label: 'No time logged', key: '6' },
  { api: 'signals', route: 'signals', label: 'Signals', key: '7' },
  { api: 'ledger', route: 'ledger', label: 'Ledger', key: '8', feature: 'billing' },
  { api: 'proposals', route: 'proposals', label: 'Proposals', key: '9' },
  { api: 'overdue_by_tech', route: 'overdue-by-tech', label: 'Overdue by technician', feature: 'contractors' },
] as const

export type QueueRouteLane = (typeof QUEUE_LANES)[number]['route'] | 'needs-us-now'

const ROUTE_ALIASES: Readonly<Record<string, QueueRouteLane>> = {
  'no-time-logged': 'no-time',
  'awaiting-invoice': 'ledger',
  'unmatched-invoices': 'ledger',
}

export function normaliseQueueRouteLane(value: string): QueueRouteLane | null {
  const route = ROUTE_ALIASES[value] ?? value
  if (route === NEEDS_US_NOW.route) return route
  return QUEUE_LANES.some((lane) => lane.route === route) ? (route as QueueRouteLane) : null
}

export function isQueueRouteLane(value: string): boolean {
  return normaliseQueueRouteLane(value) !== null
}

export function laneDefinition(route: QueueRouteLane): QueueLaneDefinition {
  if (route === NEEDS_US_NOW.route) return NEEDS_US_NOW
  const lane = QUEUE_LANES.find((candidate) => candidate.route === route)
  if (!lane) throw new Error(`Unknown queue lane: ${route}`)
  return lane
}

/** Reply/action routes remain for shortcuts and deep links, but render as filters inside the one combined view. */
export function needsUsFilter(route: QueueRouteLane): NeedsUsFilter | null {
  if (route === 'needs-us-now') return 'all'
  if (route === 'reply-owed') return 'replies'
  if (route === 'action-owed') return 'actions'
  return null
}

export function routeForNeedsUsFilter(filter: NeedsUsFilter): QueueRouteLane {
  return filter === 'replies' ? 'reply-owed' : filter === 'actions' ? 'action-owed' : 'needs-us-now'
}

export function routeForApiLane(api: ApiLane): QueueRouteLane {
  const lane = QUEUE_LANES.find((candidate) => candidate.api === api)
  if (!lane) throw new Error(`Unknown API queue lane: ${api}`)
  return lane.route as QueueRouteLane
}

/** One presentation contract for the All-cases row and the case header (#929). */
export function queueLanePresentation(api: ApiLane): QueueLanePresentation {
  const lane = QUEUE_LANES.find((candidate) => candidate.api === api)
  return {
    id: api,
    label: lane?.label ?? api.replaceAll('_', ' '),
    // All-cases rows have always used the primary lane chip. The header deliberately shares it.
    tone: 'primary',
  }
}
